import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { toast } from 'react-toastify';
import { 
  FaEnvelope, FaTrash, FaReply, FaPaperPlane, 
  FaSearch, FaUser, FaTimes, FaCheck, FaEye,
  FaPlus, FaUsers, FaUserCog, FaBuilding, FaUserTie
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';
import ConfirmDialog from '../components/ConfirmDialog';

function SubbieMessages({ onLogout }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyMode, setReplyMode] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [composeMode, setComposeMode] = useState(false);
  const [composeData, setComposeData] = useState({ recipient: '', subject: '', message: '' });
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchMessages();
    fetchUsers();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/messages?user_id=${user.id}`);
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages || []);
      } else {
        console.error('Error fetching messages:', data.error);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Subbies/clients may ONLY message admins. The backend returns just the
      // admin contact list (safe, never other tenants).
      const response = await fetch(`${API_URL}/users/admins`);
      const data = await response.json();
      if (data.success) {
        const filteredUsers = (data.users || []).filter(u => u.role === 'admin' || u.role === 'super_admin');
        setUsers(filteredUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      const response = await fetch(`${API_URL}/messages/${id}/read`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setMessages(messages.map(m => m.id === id ? { ...m, is_read: true } : m));
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null); // message id to delete

  const handleDelete = (id) => {
    setDeleteTarget(id);
  };

  const performDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await fetch(`${API_URL}/messages/${deleteTarget}?user_id=${user.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setMessages(messages.filter(m => m.id !== deleteTarget));
        if (selectedMessage?.id === deleteTarget) {
          setSelectedMessage(null);
          setReplyMode(false);
        }
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
    setDeleteTarget(null);
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedMessage) return;
    setSending(true);

    try {
      const response = await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: user.id,
          recipient_id: selectedMessage.sender_id,
          subject: `Re: ${selectedMessage.subject}`,
          message: replyText,
          parent_message_id: selectedMessage.id
        })
      });
      const data = await response.json();
      if (data.success) {
        toast('Reply sent successfully!');
        setReplyMode(false);
        setReplyText('');
        fetchMessages();
      } else {
        toast(data.error || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!composeData.recipient || !composeData.subject || !composeData.message) {
      toast('Please fill in all fields');
      return;
    }
    setSending(true);

    try {
      const response = await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: user.id,
          recipient_id: composeData.recipient,
          subject: composeData.subject,
          message: composeData.message
        })
      });
      const data = await response.json();
      if (data.success) {
        toast('Message sent successfully!');
        setComposeMode(false);
        setComposeData({ recipient: '', subject: '', message: '' });
        fetchMessages();
      } else {
        toast(data.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <FaUserCog className="text-purple-500" size={14} />;
      case 'client':
        return <FaBuilding className="text-blue-500" size={14} />;
      case 'subcontractor':
      case 'subbie':
        return <FaUserTie className="text-green-500" size={14} />;
      default:
        return <FaUser className="text-gray-400" size={14} />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'client':
        return 'Client';
      case 'subcontractor':
      case 'subbie':
        return 'Subbie';
      default:
        return role || 'User';
    }
  };

  const filteredMessages = messages.filter(m =>
    m.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.sender?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSenderName = (message) => {
    if (message.sender_id === user.id) {
      return `You → ${message.recipient?.full_name || 'Unknown'}`;
    }
    return message.sender?.full_name || 'Unknown';
  };

  const getRecipientName = (message) => {
    if (message.recipient_id === user.id) {
      return 'You';
    }
    return message.recipient?.full_name || 'Unknown';
  };

  return (
    <SubbieLayout activeTab="messages" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">💬 Messages</h1>
          <p className="text-gray-500 text-sm">View and manage all your messages</p>
        </div>
        <button
          onClick={() => setComposeMode(true)}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
        >
          <FaPaperPlane size={14} /> New Message
        </button>
      </div>

      {/* Compose Modal */}
      {composeMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setComposeMode(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">New Message</h2>
              <button onClick={() => setComposeMode(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleSendMessage} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
                <select
                  value={composeData.recipient}
                  onChange={(e) => setComposeData({ ...composeData, recipient: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  required
                >
                  <option value="">Select recipient...</option>
                  {users.filter(u => u.id !== user.id).map(u => (
                    <option key={u.id} value={u.id}>
                      {u.full_name} ({u.email}) 
                      <span className="ml-2 text-xs text-gray-400">
                        {getRoleLabel(u.role)}
                      </span>
                    </option>
                  ))}
                </select>
                {users.filter(u => u.id !== user.id).length === 0 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ No users available to message. Only admins can receive messages.
                  </p>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                <input
                  type="text"
                  value={composeData.subject}
                  onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
                <textarea
                  value={composeData.message}
                  onChange={(e) => setComposeData({ ...composeData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green min-h-[150px]"
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setComposeMode(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
                >
                  {sending ? 'Sending...' : <><FaPaperPlane className="inline mr-2" /> Send</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Message List */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden max-h-[600px] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">💬</div>
              <p>No messages</p>
            </div>
          ) : (
            filteredMessages.map((message) => (
              <div
                key={message.id}
                className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${selectedMessage?.id === message.id ? 'bg-blue-50' : ''} ${!message.is_read && message.recipient_id === user.id ? 'border-l-4 border-l-blue-500' : ''}`}
                onClick={() => {
                  setSelectedMessage(message);
                  markAsRead(message.id);
                  setReplyMode(false);
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 flex-shrink-0">
                    <FaUser size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy">{getSenderName(message)}</p>
                    <p className="text-xs text-gray-500 truncate">{message.subject}</p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(message.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 min-h-[400px]">
          {selectedMessage ? (
            <div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy">{selectedMessage.subject}</h3>
                  <p className="text-sm text-gray-500">
                    From: {selectedMessage.sender?.full_name || 'Unknown'} | To: {getRecipientName(selectedMessage)}
                  </p>
                  <p className="text-xs text-gray-400">{new Date(selectedMessage.created_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setReplyMode(!replyMode)}
                    className="px-3 py-1 border border-gray-200 rounded-lg text-sm hover:border-blue-500 hover:text-blue-500 transition-colors"
                  >
                    <FaReply className="inline mr-1" /> Reply
                  </button>
                  <button
                    onClick={() => handleDelete(selectedMessage.id)}
                    className="px-3 py-1 border border-gray-200 rounded-lg text-sm hover:border-red-500 hover:text-red-500 transition-colors"
                  >
                    <FaTrash className="inline mr-1" /> Delete
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap">
                {selectedMessage.message}
              </div>

              {/* Reply Form */}
              {replyMode && (
                <form onSubmit={handleSendReply} className="mt-4">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green min-h-[100px]"
                    required
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setReplyMode(false)}
                      className="px-3 py-1 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={sending}
                      className="px-3 py-1 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50"
                    >
                      {sending ? 'Sending...' : <><FaPaperPlane className="inline mr-1" /> Send Reply</>}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <div className="text-6xl mb-4">💬</div>
                <p>Select a message to view</p>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Message"
        message="Delete this message?"
        confirmLabel="Delete"
        danger
        onConfirm={performDelete}
      />
    </SubbieLayout>
  );
}

export default SubbieMessages;
